/**
 * Which identity document the applicant photographs, and which event says so.
 *
 * The EVENT is the choice: a step that offers several document events
 * (`kyc_thai_v4`'s `awaiting_document`) lets the applicant pick, and the event
 * fired decides the path — a Thai ID card or a driving licence goes on to its
 * back, a passport straight to the selfie. A step offering only
 * `ID_FRONT_CAPTURED` (every older seed) has nothing to choose and goes straight
 * to the camera, exactly as before.
 *
 * The country rule is the product's: the Thai documents for an issuing country
 * of Thailand, a passport for every other country.
 */
import { isAttemptStepKey, type AttemptStepKey } from './attemptReason';
import type { DocumentSide } from './capture';

export type DocumentKind = 'thai_id' | 'driving_license' | 'passport';

interface DocumentSpec {
  front: string;
  /** Null for a document with one page to photograph. */
  back: string | null;
  /** Stored file names keep the `id-` prefix: the operator review lists
   *  `id-%` images (`review.rs`), and these are what it must show. */
  fileStem: string;
}

export const DOCUMENTS: Record<DocumentKind, DocumentSpec> = {
  thai_id: { front: 'ID_FRONT_CAPTURED', back: 'ID_BACK_CAPTURED', fileStem: 'id' },
  driving_license: {
    front: 'DRIVING_LICENSE_FRONT_CAPTURED',
    back: 'DRIVING_LICENSE_BACK_CAPTURED',
    fileStem: 'id-driving-license',
  },
  passport: { front: 'PASSPORT_CAPTURED', back: null, fileStem: 'id-passport' },
};

const KINDS = Object.keys(DOCUMENTS) as DocumentKind[];

export const THAILAND = 'TH';

/** The documents an applicant may present for an issuing country. */
export function documentsFor(country: string): DocumentKind[] {
  return country === THAILAND ? ['thai_id', 'driving_license'] : ['passport'];
}

/** Documents whose FRONT this step accepts, in display order. */
export function offeredDocuments(actions: readonly string[]): DocumentKind[] {
  return KINDS.filter((kind) => actions.includes(DOCUMENTS[kind].front));
}

/** The document whose BACK this step accepts, if any. */
export function documentAwaitingBack(actions: readonly string[]): DocumentKind | null {
  return KINDS.find((kind) => {
    const back = DOCUMENTS[kind].back;
    return back !== null && actions.includes(back);
  }) ?? null;
}

export function eventFor(kind: DocumentKind, side: DocumentSide): string {
  const spec = DOCUMENTS[kind];
  return side === 'back' && spec.back ? spec.back : spec.front;
}

export function fileNameFor(kind: DocumentKind, side: DocumentSide): string {
  const stem = DOCUMENTS[kind].fileStem;
  return kind === 'passport' ? `${stem}.jpg` : `${stem}-${side}.jpg`;
}

/**
 * The attempt-reporting key: the statechart leaf the applicant is actually on
 * (the journey aggregates by it), falling back to the classic Thai-card keys
 * for a state this build does not know.
 */
export function attemptStepKey(state: string | null | undefined, side: DocumentSide): AttemptStepKey {
  const leaf = state?.split('.').pop();
  if (isAttemptStepKey(leaf)) return leaf;
  return side === 'front' ? 'awaiting_id_front' : 'awaiting_id_back';
}
